import { useRef } from 'react';
import * as React from 'react';
import styled, { css } from 'styled-components';

import type { Evidence, EvidenceFilters } from 'security-app/components/Investigations/Details/TimelineSegment/types';
import Carousel, { useSlidesInView, CAROUSEL_CONTAINER_CLASS_NAME } from 'components/common/Carousel';
import { EVIDENCE_CAROUSEL_ID } from 'security-app/components/Investigations/Details/TimelineSegment/Constants';
import useElementDimensions from 'hooks/useElementDimensions';
import EvidenceCard from 'security-app/components/Investigations/Details/TimelineSegment/EvidenceOverview/EvidenceCard';
import { defaultCompare } from 'logic/DefaultCompare';

const StyledCarousel = styled(Carousel)`
  flex: 1;

  && .${CAROUSEL_CONTAINER_CLASS_NAME} {
    height: 100%;
  }
`;

const Column = styled(Carousel.Slide)<{ $width: number }>(
  ({ $width, theme }) => css`
    flex: 0 0 ${$width}%;
    margin-right: 0;
    padding: 10px;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    gap: 5px;
    height: 100%;

    &:nth-of-type(even) {
      background-color: ${theme.colors.misc.stripedBackground};
    }
  `,
);

type Props = {
  evidencesGroupedByTime: Array<[string, Array<Evidence>]>;
  onResetShowSimilar: () => void;
  onShowSimilar: (targetEvidence: Evidence) => Promise<void>;
  similarFieldsFilter: EvidenceFilters['similar'];
  hasMessageStreamAccess: boolean;
  hasEventsStreamAccess: boolean;
};

const EvidenceCarousel = ({
  evidencesGroupedByTime,
  onResetShowSimilar,
  onShowSimilar,
  similarFieldsFilter,
  hasMessageStreamAccess,
  hasEventsStreamAccess,
}: Props) => {
  const slidesInView = useSlidesInView(EVIDENCE_CAROUSEL_ID, true);
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useElementDimensions(containerRef);
  const slideWidth = containerWidth ? 100 / Math.round(containerWidth / 300) : 0; // width in %

  return (
    <StyledCarousel containerRef={containerRef} carouselId={EVIDENCE_CAROUSEL_ID}>
      {!!containerWidth &&
        evidencesGroupedByTime.map(([timestamp, evidenceGroup], index) => (
          <Column $width={slideWidth} key={timestamp}>
            {evidenceGroup
              .sort((evidenceA, evidenceB) => defaultCompare(evidenceA.timestamp, evidenceB.timestamp))
              .map((evidence) => (
                <EvidenceCard
                  evidence={evidence}
                  onShowSimilar={onShowSimilar}
                  onResetShowSimilar={onResetShowSimilar}
                  similarFieldsFilter={similarFieldsFilter}
                  key={`${evidence.id}-${!!similarFieldsFilter}`}
                  isInView={slidesInView.includes(index)}
                  hasMessageStreamAccess={hasMessageStreamAccess}
                  hasEventsStreamAccess={hasEventsStreamAccess}
                />
              ))}
          </Column>
        ))}
    </StyledCarousel>
  );
};

export default EvidenceCarousel;
